import appClient, { ResultApi } from "../../common/services/AppClient";
import {
    ActionConstraint,
    ActionDefinition,
    ActionExecutionPermissions,
    ActionField,
    AiLookupItem,
    ActionType,
    PayloadStructure,
} from "../types/Actions";
import security from "../../common/services/SecurityService";
import { Utils } from "src/modules/common/misc/Utils";
import { endpoints } from "../../../config";

export class ActionService {
    uriPrefix = endpoints.tasksUrl + "actions";

    getActionTypes() {
        const url = this.uriPrefix + "/types";
        return appClient.get<ActionType[]>(url);
    }

    async getActionDefinitions(projectId?: string) {
        let url = this.uriPrefix;
        if (projectId) {
            url += `/project/${projectId}`;
        }

        const resp = await appClient.get<ActionDefinition[]>(url);
        return resp.mapErr((err) => new Error(err.data?.title));
    }

    getAiProjects() {
        return appClient.get<AiLookupItem[]>(`${this.uriPrefix}/ai/projects`);
    }

    getAiApplications(projectId: string) {
        return appClient.get<AiLookupItem[]>(
            `${this.uriPrefix}/ai/projects/${projectId}/applications`,
        );
    }

    createActionDefinition(formData: FormData) {
        return appClient.post<string>(this.uriPrefix, formData);
    }

    deleteActionDefinition(actionDefinitionId: string) {
        const url = this.uriPrefix + `/${actionDefinitionId}`;
        return appClient.delete(url);
    }

    updateActionDefinition(
        actionDefinitionId: string,
        actionType: string,
        name: string,
        title: string,
        fields: ActionField[] | [],
        constraints: ActionConstraint[] | [],
        isConfirmationRequired: boolean,
        permissions?: ActionExecutionPermissions,
        payloadStructure?: PayloadStructure,
    ) {
        const request = {
            actionType,
            name,
            title,
            fields,
            constraints,
            isConfirmationRequired,
            permissions: permissions ?? { admin: false, roles: [] },
            payloadStructure,
        };
        const url = this.uriPrefix + `/${actionDefinitionId}`;
        return appClient.patch(url, request);
    }

    importActionDefinition(
        formData: FormData,
        projectId: string,
    ): Promise<ResultApi<string>> {
        const url = `${this.uriPrefix}/project/${projectId}/import`;
        return appClient.post(url, formData);
    }

    async exportActionDefinition(id: string) {
        const mapForm = document.createElement("form");
        mapForm.setAttribute("id", "actionDefExportPostForm");
        mapForm.target = "_blank";
        mapForm.method = "POST";

        await security.invoke((token) => {
            const t = "?access_token=" + encodeURIComponent(token);
            const url = `${this.uriPrefix}/export/${id}${t}`;
            Utils.downloadFile(url, mapForm, "actionDefExportPostForm");
            return Promise.resolve();
        });
    }

    updateActionsListOrder(
        id: string,
        dragIndex: number,
        dropIndex: number,
        lastModified: string | null,
    ) {
        const request = {
            dragIndex,
            dropIndex,
            lastModified,
        };
        const url = `${this.uriPrefix}/position/update/${id}`;
        return appClient.patch(url, request);
    }
}
